import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "i18next";
import { CSSProperties, useMemo } from "react";

import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { spacing } from "#app/theme/v1/spacing";
import { UpdateProcessStepResponseDto } from "#modules/processSteps/infra/updateProcessStep.api";
import { Alert } from "#modules/quoteRequestLines/domain/alert";
import {
  ProcessStep,
  PartialProcessStep,
} from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStep";
import { Icon } from "#shared/icons/Icon";
import { Paper } from "#shared/view/components/Paper";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Table } from "#shared/view/designSystem/Table";
import { MaterialLayout, ProductLine, ProcessStep as ProcessStepType } from "shared";

type RowDragHandleCellProps = {
  rowId: string;
};

const RowDragHandleCell = ({ rowId }: RowDragHandleCellProps) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    <span
      {...attributes}
      {...listeners}
      style={{ cursor: "grab", display: "flex", alignItems: "center" }}
    >
      <Icon name="gripVertical" size={12} color={colors.gray[5]} />
    </span>
  );
};

type DraggableRowProps = {
  rowId: string;
  children: React.ReactNode;
};

const DraggableRow = ({ rowId, children }: DraggableRowProps) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: rowId,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
  };

  return (
    <Table.Tr ref={setNodeRef} style={style}>
      {children}
    </Table.Tr>
  );
};

export type ProcessStepForTable = ProcessStepType & {
  materialLayout: MaterialLayout | null;
  productLines: ProductLine[];
};

type ProcessStepsTableProps = {
  processSteps: ProcessStepForTable[];
  showCompanyMachines: boolean;
  onProcessStepUpdated?: (updatedProcessStep: UpdateProcessStepResponseDto) => void;
  onProcessStepDeleted?: (index: number) => void;
  onReorder?: (reorderedProcessSteps: PartialProcessStep[]) => void;
  readonly?: boolean;
  alerts?: Alert[];
};

export const ProcessStepsTable = ({
  showCompanyMachines,
  processSteps,
  onProcessStepUpdated,
  onProcessStepDeleted,
  onReorder,
  readonly = false,
  alerts = [],
}: ProcessStepsTableProps) => {
  const hasRecommendedProcessSteps = processSteps.length > 0;

  const dataIds = useMemo<UniqueIdentifier[]>(
    () => processSteps.map((ps, index) => ps.id || `temp-${index}`),
    [processSteps],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onReorder) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      const reorderedData = arrayMove([...processSteps], oldIndex, newIndex);
      onReorder(reorderedData as PartialProcessStep[]);
    }
  };

  const sortable = !readonly && !!onReorder;

  const tableContent = (
    <Stack gap={"xxs"}>
      {hasRecommendedProcessSteps && (
        <Paper radius={"sm"} bg={colors.gray[1]}>
          <Table
            layout="fixed"
            w="100%"
            styles={{
              td: { padding: spacing.xxxs, fontSize: fontSizes.tiny },
              th: { padding: spacing.xxxs, fontSize: fontSizes.tiny },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                {sortable && <Table.Th style={{ width: 30 }} />}
                {!readonly && <Table.Th style={{ width: "5%" }} />}
                <Table.Th style={{ width: showCompanyMachines ? "20%" : "30%" }}>
                  {t("quoteRequestLine.processSteps")}
                </Table.Th>
                {showCompanyMachines && (
                  <Table.Th style={{ width: "15%" }}>
                    {t("quoteRequestLine.companyMachine")}
                  </Table.Th>
                )}
                <Table.Th style={{ width: "15%" }}>
                  {t("processStep.machinePreparationTime")}
                </Table.Th>
                <Table.Th style={{ width: "15%" }}>{t("processStep.otherMachineTime")}</Table.Th>
                <Table.Th style={{ width: "10%" }}>{t("processStep.machineHourlyPrice")}</Table.Th>
                <Table.Th style={{ width: "10%" }}>{t("processStep.unitCost")}</Table.Th>
                {!readonly && <Table.Th style={{ width: "5%" }} />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortable ? (
                <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                  {processSteps.map((ps, index) => {
                    const rowId = ps.id || `temp-${index}`;
                    return (
                      <DraggableRow key={rowId} rowId={rowId}>
                        <Table.Td>
                          <RowDragHandleCell rowId={rowId} />
                        </Table.Td>
                        <ProcessStep
                          showCompanyMachines={showCompanyMachines}
                          readonly={readonly}
                          index={index}
                          processStep={ps}
                          alerts={alerts}
                          onProcessStepUpdated={onProcessStepUpdated}
                          onProcessStepDeleted={() => onProcessStepDeleted?.(index)}
                          renderAsFragment
                        />
                      </DraggableRow>
                    );
                  })}
                </SortableContext>
              ) : (
                processSteps.map((ps, index) => (
                  <ProcessStep
                    key={ps.id || `temp-${index}`}
                    showCompanyMachines={showCompanyMachines}
                    readonly={readonly}
                    index={index}
                    processStep={ps}
                    alerts={alerts}
                    onProcessStepUpdated={onProcessStepUpdated}
                    onProcessStepDeleted={() => onProcessStepDeleted?.(index)}
                  />
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {!hasRecommendedProcessSteps && <Text c={colors.gray[4]}>Aucun procédé de fabrication</Text>}
    </Stack>
  );

  if (!sortable) {
    return tableContent;
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      {tableContent}
    </DndContext>
  );
};
